import { t } from "i18next";
import { UploadCloudIcon } from "lucide-react";
import type { JSX } from "react";
import { underV3 } from "@/components/ui/v3/flagged";
import { useBackgroundVisitStatus } from "@/hooks/backgroundVisitUpload/useBackgroundVisitStatus";
import { useUnloadWarning } from "@/hooks/useUnloadWarning";
import { cn } from "@/lib/utils";
import { LoadingSpinner } from "../loading-spinner";

interface Props {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export const OpenBackgroundVisitUploadButton = (props: Props): JSX.Element => {
  const { isUploading, unsentCount } = useBackgroundVisitStatus();

  // Failed uploads still hold the only copy of the audio in this tab.
  useUnloadWarning(
    unsentCount !== 0,
    t("backgroundVisitUpload.closePageMessage"),
  );

  return (
    <button
      type="button"
      aria-label={t("backgroundVisitUpload.openBackgroundUploadsPanel")}
      className={cn(
        "relative p-2 rounded-md hover:bg-muted hover:text-muted-foreground transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary",
        underV3(
          "flex size-11 items-center justify-center rounded-full p-0 text-v3-text-2 hover:bg-v3-surface-2 hover:text-v3-text focus-visible:ring-v3-brand-ring lg:size-9",
        ),
      )}
      onClick={() => {
        props.onOpenChange(!props.isOpen);
      }}
    >
      <UploadCloudIcon
        className={cn("w-7 h-7 text-primary", underV3("size-5 text-current"))}
      />
      {unsentCount !== 0 && (
        <span
          className={cn(
            "absolute top-0 right-0 bg-error text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center",
            underV3("bg-v3-rec font-v3 text-[11px]"),
          )}
        >
          {isUploading ? (
            <LoadingSpinner className="w-3 h-3" />
          ) : (
            <>{unsentCount}</>
          )}
        </span>
      )}
    </button>
  );
};
